/**
 * Debug Store — Zustand store for the Agent Debug Suite.
 * Serves as event bus, session manager, and localStorage persistence layer.
 * Only active when isPlatformAdmin is true (isEnabled flag).
 */
import { z } from 'zod';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { ReasoningStep, AgentVisualResponse } from '@/services/api/agentApi';
import type { CloningDetail } from '@/services/editor/CanvasActionApplicator';
import type { PlanLLMDebugCall } from '@/types/plan';
import {
  classifyRoute,
  type RoutingContext,
} from '@/services/editor/AgentChatController';

// --- Types ---

export interface SSEDebugEvent {
  type: string;
  data: unknown;
  timestamp: number;
}

export interface ApplicatorResult {
  step: string;
  applied: string[];
  failed: string[];
  cloningDetails?: CloningDetail[];
  timestamp: number;
}


// ── Plan Debug Data ──────────────────────────────────────────────────────

export type RetryType = 'initial' | 'ratio-or-count' | 'coverage';

export interface RetryAttempt {
  step: string;                // e.g. "Interaction"
  agent: string;                // e.g. "component"
  attemptNumber: number;
  retryType: RetryType;
  startedAt: number;            // Date.now() when recorded in frontend
  durationMs: number | null;    // filled in when retry_result arrives
  actionCount: number | null;   // filled in when retry_result arrives
  accepted: boolean | null;     // did this attempt win the improved-gate comparison?
  potentialFalsePositive: boolean;
  reason?: string;
  previousAttempt?: {
    durationMs?: number;
    actionCount?: number;
    interactionRatio?: number;
  };
  gateValues?: Record<string, number | string>;
  metrics?: Record<string, number>;
}

export interface SubAgentAttemptsSummary {
  step: string;
  agent: string;
  totalAttempts: number;
  totalDurationMs: number;
  wastedDurationMs: number;
  attempts: Array<{
    attemptNumber: number;
    retryType: RetryType;
    startedAt: number;
    durationMs: number;
    actionCount: number;
    accepted: boolean;
  }>;
}

export interface PipelineTiming {
  pipelineType: string;
  wallClockMs: number;
  sumOfDurationsMs: number;
  parallelizationSavingsMs: number;
  componentBatchConcurrency: number;
  durations: {
    dataDiscovery?: number;
    layout?: number;
    components?: number;
    styles?: number;
    dataComponents?: number;
    dataBinding?: number;
  };
  offsets: {
    ddKickoff?: number;
    ddResolved?: number;
    ddAwaited?: number;
    layoutStart?: number;
    layoutEnd?: number;
    componentsStart?: number;
    componentsEnd?: number;
    stylesKickoff?: number;
    stylesResolved?: number;
    stylesAwaited?: number;
    dcStart?: number;
    dcEnd?: number;
    dbStart?: number;
    dbEnd?: number;
    pipelineEnd?: number;
  };
}

export interface RuntimeDataTextDiagnostic {
  timestamp: number;
  componentId: string;
  mode: 'query-template' | 'computed' | 'workflow' | 'timestamp-aggregate' | 'static';
  authUserPresent?: boolean;
  queryTemplateId?: string;
  valueField?: string;
  rowCount?: number;
  rowKeys?: string[];
  computedExpressionPreview?: string;
  expressionDependencies?: string[];
  dependencySummary?: Array<{
    id: string;
    rowCount: number;
    rowKeys: string[];
    error?: string;
  }>;
  referenceSummary?: Array<{
    reference: string;
    dependencyId?: string;
    path?: string;
    rowCount?: number;
    rowKeys?: string[];
    status: string;
    resolvedValueType?: string;
  }>;
  allDepsLoaded?: boolean;
  extractedValueType?: string;
  usedFallback?: boolean;
  fallbackReason?: string;
  error?: string | null;
}

export interface RuntimeFormSubmitDiagnostic {
  timestamp: number;
  componentId: string;
  status: 'ready' | 'started' | 'blocked' | 'success' | 'error';
  reason?: string;
  action?: string;
  interactionMode?: string;
  canInteract?: boolean;
  hasInteractionHandlers?: boolean;
  groupId?: string;
  schemaId?: string;
  submitMode?: 'create' | 'upsert';
  apiOperation?: 'create' | 'update';
  upsertMatched?: boolean;
  writtenRecordId?: string;
  apiResponseKeys?: string[];
  preSubmitRecordCount?: number;
  postSubmitRecordCount?: number;
  readBackFound?: boolean;
  readBackError?: string;
  schemaFetchError?: string;
  refreshResults?: Array<{
    targetId: string;
    method: 'refetch' | 'fallback-query';
    status: 'success' | 'error';
    error?: string;
  }>;
  mappedFields?: string[];
  payloadFields?: string[];
  refreshTargets?: string[];
  authUserPresent?: boolean;
  hasUserId?: boolean;
  validationErrors?: string[];
  error?: string;
}

export interface RuntimeQueryTemplateDiagnostic {
  timestamp: number;
  componentId: string;
  status: 'skipped' | 'started' | 'success' | 'error';
  reason?: string;
  mode?: 'template' | 'pipeline' | 'workflow';
  dataSourceType?: string;
  queryTemplateId?: string;
  schemaId?: string;
  groupId?: string;
  organizationId?: string;
  measures?: string[];
  filters?: string[];
  orderBy?: string[];
  groupBy?: string[];
  limit?: number;
  hasAiPipeline?: boolean;
  authUserPresent?: boolean;
  shouldExecute?: boolean;
  force?: boolean;
  rowCount?: number;
  rowKeys?: string[];
  executionTimeMs?: number;
  cached?: boolean;
  cacheKey?: string;
  skipCache?: boolean;
  templateUserOrgId?: string;
  templateDataOrgId?: string;
  templateQuerySummary?: {
    schemaId?: string;
    groupId?: string;
    measures?: string[];
    filters?: string[];
    orderBy?: string[];
    groupBy?: string[];
    timeRange?: string;
    limit?: number;
  };
  httpStatus?: number;
  error?: string;
}

export interface PlanPhaseDebugData {
  phaseId: string;
  phaseTitle: string;
  domain: string;
  enrichedPrompt: string;
  startedAt: number;
  endedAt: number | null;
  duration: number | null;
  subAgentEvents: SSEDebugEvent[];
  subAgentSteps: Array<{
    agentName: string;
    step: string;
    startedAt: number;
    duration: number | null;
    actionsCount: number;
    tokenUsage?: { tokensInput?: number; tokensOutput?: number; estimatedCostUsd?: number };
  }>;
  actionsProduced: number;
  componentIdsCreated: string[];
  pageIdsCreated: string[];
  implAuditResult: Record<string, unknown> | null;
  retried: boolean;
  retries?: RetryAttempt[];
  retrySummary?: SubAgentAttemptsSummary;
  pipelineTiming?: PipelineTiming;
  subAgentThreadId: string | null;
  subAgentDiagnostics: Record<string, unknown> | null;
  error: string | null;
  // Debug enhancements (1-6)
  classification?: {
    pipeline: string;
    complexity: string;
    isFollowUp: boolean;
    followUpAgent?: string;
    mqttConnected?: boolean;
    classificationSource?: string;
  };
  dataDiscovery?: {
    attempted: boolean;
    duration_ms: number;
    matchedSchemas: number;
    failed: boolean;
  };
  actionBreakdown?: Record<string, number>;
  tokenUsage?: { tokensInput: number; tokensOutput: number; estimatedCostUsd: number };
  verificationResults?: Array<{
    step: string;
    passed: boolean;
    issues: string[];
    componentCount?: number;
    containerCount?: number;
  }>;
  unresolvedTokens?: string[];
  diagnosticMessages?: string[];
  actionSummaries?: ActionPayloadSummary[];
  schemaQueryAlignment?: SchemaQueryAlignment;
}

export interface ActionPayloadSummary {
  index: number;
  type: string;
  componentId: string;
  componentName?: string;
  changedKeys: string[];
}

export interface SchemaQueryAlignment {
  schemaAlignments: Array<{
    schemaName: string;
    schemaId: string;
    expectedQueryCount: number;
    actualQueryCount: number;
    matchedQueries: string[];
  }>;
  queryAlignments: Array<{
    expectedQueryName: string;
    targetSchema: string;
    matched: boolean;
    matchedTemplateName?: string;
    matchedTemplateId?: string;
  }>;
  issues: string[];
  aligned: boolean;
}

export interface PlanQualityGateEvent {
  timestamp: number;
  stage: string;
  phaseId?: string;
  verdict: string;
  score?: number;
  action: string;
  retryNumber?: number;
  reason?: string;
  warnings?: string[];
  invalidReferences?: string[];
  blockingIssues?: string[];
}

export interface PlanStageTimeline {
  stage: string;
  label: string;
  startedAt: number;
  endedAt: number | null;
  duration: number | null;
}

export interface PlanDebugData {
  pipelineTimeline: PlanStageTimeline[];
  phaseExecutions: Record<string, PlanPhaseDebugData>;
  qualityGates: PlanQualityGateEvent[];
  stagePrompts: PlanLLMDebugCall[];
  runtimeDiagnostics: RuntimeDataTextDiagnostic[];
  formSubmitDiagnostics: RuntimeFormSubmitDiagnostic[];
  queryTemplateDiagnostics: RuntimeQueryTemplateDiagnostic[];
  pipelineStartedAt: number | null;
  pipelineEndedAt: number | null;
  isActive: boolean;
}

export interface ClassificationDivergence {
  input: string;
  regexRoute: string;
  llmRoute: string;
  llmConfidence: number;
  context: {
    hasSelectedComponent: boolean;
    hasSelectedComponentType?: string;
    agentAvailable: boolean;
    hasSessionContext: boolean;
  };
  timestamp: number;
}

export interface DebugSession {
  id: string;
  timestamp: number;
  threadId: string | null;
  userMessage: string;
  routeDecision: string;
  routePatternMatched: string | null;

  // Pipeline flow
  sseEvents: SSEDebugEvent[];

  // Reasoning
  reasoningChain: ReasoningStep[];

  // Actions
  actionsApplied: string[];
  actionsFailed: string[];
  applicatorResults: ApplicatorResult[];
  confidenceDistribution: Array<{
    actionType: string;
    confidence: number;
    agentDomain: string;
  }>;

  // Context
  editorContext: unknown | null;
  sessionContext: unknown | null;

  // Outcome
  feedbackSignal: 'positive' | 'negative' | null;
  correctionText: string | null;
  snapshotDiff: {
    added: string[];
    removed: string[];
    modified: Array<{ componentId: string; changes: string[] }>;
    summary: string;
  } | null;

  // Performance
  totalDuration: number | null;
  stepDurations: Record<string, number>;
  streamingStartedAt: number | null;
  streamingEndedAt: number | null;

  // Completion data
  completionData: AgentVisualResponse | null;

  // Backend pipeline diagnostics (from 'diagnostic' SSE event)
  diagnostics: {
    complexity?: string;
    pipeline?: string;
    agentType?: string;
    isFollowUp?: boolean;
    followUpAgent?: string;
    hasSessionContext?: boolean;
    selectedComponentCount?: number;
    mqttConnected?: boolean;
  } | null;

  // Component manifest events (from 'component_manifest' SSE event)
  componentManifestEvents: Array<{
    agentType: string;
    componentCount: number;
    componentIds: string[];
    source: 'selection' | 'auto-extracted';
    timestamp: number;
  }>;
}

interface DebugStoreState {
  isEnabled: boolean;
  currentSession: DebugSession | null;
  sessions: DebugSession[];
  compareSessionId: string | null;
  classificationDivergences: ClassificationDivergence[];
  planDebugData: PlanDebugData | null;
}

interface DebugStoreActions {
  enable: () => void;
  disable: () => void;
  startSession: (userMessage: string, routeDecision: string) => void;
  setEditorContext: (ctx: unknown) => void;
  setSessionContext: (ctx: unknown) => void;
  setRoutePatternMatched: (input: string, context: RoutingContext) => void;
  pushEvent: (event: SSEDebugEvent) => void;
  flushEvents: () => void;
  recordApplicatorResult: (step: string, result: { applied: string[]; failed: string[]; cloningDetails?: CloningDetail[] }) => void;
  setStreamingStart: () => void;
  setStreamingEnd: () => void;
  setReasoningChain: (chain: ReasoningStep[]) => void;
  setCompletionData: (response: AgentVisualResponse) => void;
  setFeedbackSignal: (signal: 'positive' | 'negative', correctionText?: string) => void;
  setThreadId: (threadId: string) => void;
  endSession: () => void;
  loadSession: (id: string) => void;
  deleteSession: (id: string) => void;
  clearAllSessions: () => void;
  setCompareSession: (id: string | null) => void;
  hydrateFromStorage: () => void;
  recordClassificationDivergence: (divergence: Omit<ClassificationDivergence, 'timestamp'>) => void;
  clearClassificationDivergences: () => void;
  pushComponentManifestEvent: (event: { agentType: string; componentCount: number; componentIds: string[]; source: 'selection' | 'auto-extracted' }) => void;

  // Plan debug actions
  initPlanDebug: () => void;
  endPlanDebug: () => void;
  recordPlanStageTransition: (stage: string, label: string) => void;
  completePlanStage: (stage: string) => void;
  initPhaseExecution: (phaseId: string, title: string, domain: string, enrichedPrompt: string) => void;
  pushPhaseSubAgentEvent: (phaseId: string, event: SSEDebugEvent) => void;
  recordPhaseSubAgentStep: (phaseId: string, step: { agentName: string; step: string; startedAt: number; duration: number | null; actionsCount: number }) => void;
  recordPhaseRetry: (phaseId: string, retry: {
    step: string;
    agent: string;
    attemptNumber: number;
    retryType: RetryType;
    reason?: string;
    potentialFalsePositive?: boolean;
    previousAttempt?: RetryAttempt['previousAttempt'];
    gateValues?: Record<string, number | string>;
  }) => void;
  recordPhaseRetryResult: (phaseId: string, result: {
    step: string;
    attemptNumber: number;
    retryType: RetryType;
    durationMs: number;
    actionCount: number;
    improved: boolean;
    acceptedAsResult: boolean;
    metrics?: Record<string, number>;
  }) => void;
  recordPhaseAttemptsSummary: (phaseId: string, summary: SubAgentAttemptsSummary) => void;
  recordPhasePipelineTiming: (phaseId: string, timing: PipelineTiming) => void;
  completePhaseExecution: (phaseId: string, data: { implAuditResult?: unknown; retried?: boolean; actionsProduced?: number; componentIds?: string[]; pageIds?: string[]; error?: string; actionBreakdown?: Record<string, number>; unresolvedTokens?: string[] }) => void;
  setPhaseUnresolvedTokens: (phaseId: string, tokens: string[]) => void;
  recordPlanQualityGate: (gate: Omit<PlanQualityGateEvent, 'timestamp'>) => void;
  recordPlanStagePrompts: (calls: PlanLLMDebugCall[]) => void;
  appendActionSummaries: (phaseId: string, summaries: ActionPayloadSummary[]) => void;
  replaceActionSummaries: (phaseId: string, summaries: ActionPayloadSummary[]) => void;
  setSchemaQueryAlignment: (phaseId: string, alignment: SchemaQueryAlignment) => void;
  recordRuntimeDataTextDiagnostic: (diagnostic: Omit<RuntimeDataTextDiagnostic, 'timestamp'>) => void;
  recordRuntimeFormSubmitDiagnostic: (diagnostic: Omit<RuntimeFormSubmitDiagnostic, 'timestamp'>) => void;
  recordRuntimeQueryTemplateDiagnostic: (diagnostic: Omit<RuntimeQueryTemplateDiagnostic, 'timestamp'>) => void;
  clearPlanDebug: () => void;
}

// --- Constants ---

const STORAGE_KEY = 'terramatrix_debug_sessions';
const MAX_SESSIONS = 20;
const FLUSH_INTERVAL = 200; // ms
const MAX_EVENTS_ACTIONS_PAYLOAD = 50;
const MAX_RUNTIME_DIAGNOSTICS = 200;
const MAX_FORM_SUBMIT_DIAGNOSTICS = 100;
const MAX_QUERY_TEMPLATE_DIAGNOSTICS = 200;

const SseDebugEventSchema = z.object({
  type: z.string(),
  data: z.unknown(),
  timestamp: z.number(),
}).transform((value): SSEDebugEvent => ({
  type: value.type,
  data: value.data,
  timestamp: value.timestamp,
}));

const CloningDetailSchema = z.object({
  componentId: z.string(),
  componentType: z.string(),
  donorId: z.string().nullable(),
  donorType: z.string().nullable(),
  isSameType: z.boolean(),
  isCousinMatch: z.boolean(),
  cousinParentId: z.string().nullable(),
  siblingCount: z.number(),
  clonedStyle: z.array(z.string()),
  clonedProperties: z.array(z.string()),
  clonedBreakpointStyles: z.record(z.array(z.string())),
});

const ApplicatorResultSchema = z.object({
  step: z.string(),
  applied: z.array(z.string()),
  failed: z.array(z.string()),
  cloningDetails: z.array(CloningDetailSchema).optional(),
  timestamp: z.number(),
});

const ReasoningStepSchema = z.object({
  agent: z.string(),
  step: z.string(),
  reasoning: z.string(),
  alternatives: z.array(z.object({
    description: z.string(),
    whyNotChosen: z.string(),
  })).optional(),
  confidence: z.number(),
  memoryInfluence: z.array(z.string()).optional(),
  duration_ms: z.number().optional(),
});

const CanvasActionSchema = z.object({
  type: z.enum([
    'add_component',
    'update_component',
    'delete_component',
    'add_page',
    'update_page',
    'reorder_children',
    'batch_update',
    'replace_page',
    'switch_page',
    'link_pages',
    'create_branch',
    'move_component',
  ]),
  payload: z.object({
    componentId: z.string().optional(),
    pageId: z.string().optional(),
    component: z.record(z.unknown()).optional(),
    changes: z.record(z.unknown()).optional(),
    components: z.array(z.record(z.unknown())).optional(),
    parentId: z.string().optional(),
    position: z.number().optional(),
    breakpoint: z.enum(['mobile', 'tablet', 'desktop']).optional(),
    newOrder: z.array(z.string()).optional(),
    pageName: z.string().optional(),
    pageDescription: z.string().optional(),
    pageOrder: z.number().optional(),
    targetPageIds: z.array(z.string()).optional(),
    navigationConfig: z.record(z.unknown()).optional(),
    branchName: z.string().optional(),
    branchDescription: z.string().optional(),
    oldParentId: z.string().optional(),
    id: z.string().optional(),
  }),
  metadata: z.object({
    agentId: z.string(),
    agentDomain: z.string(),
    reasoning: z.string().optional(),
    confidence: z.number().optional(),
  }).optional(),
});

const ProactiveSuggestionSchema = z.object({
  id: z.string(),
  category: z.enum([
    'accessibility',
    'brand-compliance',
    'layout',
    'data-opportunity',
    'performance',
    'ux',
  ]),
  severity: z.enum(['info', 'warning', 'critical']),
  title: z.string(),
  description: z.string(),
  reasoning: z.string().optional(),
  confidence: z.number(),
  suggestedActions: z.array(CanvasActionSchema).optional(),
  affectedComponentIds: z.array(z.string()).optional(),
  wcagCriteria: z.string().optional(),
});

const AccessibilityViolationSchema = z.object({
  componentId: z.string(),
  componentName: z.string().optional(),
  rule: z.string(),
  wcagCriteria: z.string(),
  severity: z.enum(['minor', 'moderate', 'serious', 'critical']),
  description: z.string(),
  fixActions: z.array(CanvasActionSchema).optional(),
});

const AccessibilityAuditResultSchema = z.object({
  score: z.number(),
  totalChecks: z.number(),
  passes: z.number(),
  violations: z.array(AccessibilityViolationSchema),
  summary: z.string(),
});

const AgentVisualResponseSchema = z.object({
  actions: z.array(CanvasActionSchema).optional(),
  message: z.string(),
  followUpSuggestions: z.array(z.string()).optional(),
  requiresConfirmation: z.boolean().optional(),
  threadId: z.string().optional(),
  memoriesCreated: z.number().optional(),
  memoriesReinforced: z.number().optional(),
  reasoningChain: z.array(ReasoningStepSchema).optional(),
  proactiveSuggestions: z.array(ProactiveSuggestionSchema).optional(),
  accessibilityAudit: AccessibilityAuditResultSchema.optional(),
}).transform((value): AgentVisualResponse => ({
  actions: value.actions,
  message: value.message,
  followUpSuggestions: value.followUpSuggestions,
  requiresConfirmation: value.requiresConfirmation,
  threadId: value.threadId,
  memoriesCreated: value.memoriesCreated,
  memoriesReinforced: value.memoriesReinforced,
  reasoningChain: value.reasoningChain,
  proactiveSuggestions: value.proactiveSuggestions,
  accessibilityAudit: value.accessibilityAudit,
}));

const DebugSessionSchema = z.object({
  id: z.string(),
  timestamp: z.number(),
  threadId: z.string().nullable(),
  userMessage: z.string(),
  routeDecision: z.string(),
  routePatternMatched: z.string().nullable(),
  sseEvents: z.array(SseDebugEventSchema),
  reasoningChain: z.array(ReasoningStepSchema),
  actionsApplied: z.array(z.string()),
  actionsFailed: z.array(z.string()),
  applicatorResults: z.array(ApplicatorResultSchema),
  confidenceDistribution: z.array(z.object({
    actionType: z.string(),
    confidence: z.number(),
    agentDomain: z.string(),
  })),
  editorContext: z.unknown().nullable(),
  sessionContext: z.unknown().nullable(),
  feedbackSignal: z.union([z.literal('positive'), z.literal('negative')]).nullable(),
  correctionText: z.string().nullable(),
  snapshotDiff: z.object({
    added: z.array(z.string()),
    removed: z.array(z.string()),
    modified: z.array(z.object({
      componentId: z.string(),
      changes: z.array(z.string()),
    })),
    summary: z.string(),
  }).nullable(),
  totalDuration: z.number().nullable(),
  stepDurations: z.record(z.number()),
  streamingStartedAt: z.number().nullable(),
  streamingEndedAt: z.number().nullable(),
  completionData: AgentVisualResponseSchema.nullable(),
  diagnostics: z.object({
    complexity: z.string().optional(),
    pipeline: z.string().optional(),
    agentType: z.string().optional(),
    isFollowUp: z.boolean().optional(),
    followUpAgent: z.string().optional(),
    hasSessionContext: z.boolean().optional(),
    selectedComponentCount: z.number().optional(),
    mqttConnected: z.boolean().optional(),
  }).nullable(),
  componentManifestEvents: z.array(z.object({
    agentType: z.string(),
    componentCount: z.number(),
    componentIds: z.array(z.string()),
    source: z.enum(['selection', 'auto-extracted']),
    timestamp: z.number(),
  })).default([]),
}).transform((value): DebugSession => ({
  id: value.id,
  timestamp: value.timestamp,
  threadId: value.threadId,
  userMessage: value.userMessage,
  routeDecision: value.routeDecision,
  routePatternMatched: value.routePatternMatched,
  sseEvents: value.sseEvents,
  reasoningChain: value.reasoningChain,
  actionsApplied: value.actionsApplied,
  actionsFailed: value.actionsFailed,
  applicatorResults: value.applicatorResults,
  confidenceDistribution: value.confidenceDistribution,
  editorContext: value.editorContext,
  sessionContext: value.sessionContext,
  feedbackSignal: value.feedbackSignal,
  correctionText: value.correctionText,
  snapshotDiff: value.snapshotDiff,
  totalDuration: value.totalDuration,
  stepDurations: value.stepDurations,
  streamingStartedAt: value.streamingStartedAt,
  streamingEndedAt: value.streamingEndedAt,
  completionData: value.completionData,
  diagnostics: value.diagnostics,
  componentManifestEvents: value.componentManifestEvents,
}));

// --- Module-scoped batching state (non-reactive) ---

let _pendingEvents: SSEDebugEvent[] = [];
let _flushTimer: ReturnType<typeof setTimeout> | null = null;
let _planModeActive = false;

// --- Pattern replay (uses IntentClassifier via classifyRoute) ---

async function replayPatternMatch(input: string, context: RoutingContext): Promise<string> {
  const trimmed = input.trim();
  if (!trimmed) return 'empty_input';
  if (!context.agentAvailable) return 'agent_unavailable';

  try {
    const result = await classifyRoute(trimmed, context);
    return `${result.source}:${result.route}`;
  } catch {
    return 'classification_error';
  }
}

// --- localStorage helpers ---

function loadFromStorage(): DebugSession[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    const result = z.array(z.unknown()).safeParse(parsed);
    if (!result.success) return [];

    return result.data.flatMap((session): DebugSession[] => {
      const parsedSession = DebugSessionSchema.safeParse(session);
      return parsedSession.success ? [parsedSession.data] : [];
    });
  } catch {
    return [];
  }
}

function saveToStorage(sessions: DebugSession[]): void {
  try {
    // Truncate large action payloads before persisting
    const trimmed = sessions.map(s => ({
      ...s,
      sseEvents: s.sseEvents.map(e => {
        if (e.type === 'actions' && e.data && typeof e.data === 'object') {
          const d = e.data as Record<string, unknown>;
          const actions = d.actions;
          if (Array.isArray(actions) && actions.length > MAX_EVENTS_ACTIONS_PAYLOAD) {
            return {
              ...e,
              data: {
                ...d,
                actions: actions.slice(0, MAX_EVENTS_ACTIONS_PAYLOAD),
                _truncated: true,
                _originalCount: actions.length,
              },
            };
          }
        }
        return e;
      }),
    }));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch (err) {
    // Quota exceeded — evict oldest and retry
    if (sessions.length > 1) {
      saveToStorage(sessions.slice(1));
    } else {
      console.warn('[DebugStore] Failed to persist debug sessions', err);
    }
  }
}

function createEmptySession(userMessage: string, routeDecision: string): DebugSession {
  return {
    id: crypto.randomUUID(),
    timestamp: Date.now(),
    threadId: null,
    userMessage,
    routeDecision,
    routePatternMatched: null,
    sseEvents: [],
    reasoningChain: [],
    actionsApplied: [],
    actionsFailed: [],
    applicatorResults: [],
    confidenceDistribution: [],
    editorContext: null,
    sessionContext: null,
    feedbackSignal: null,
    correctionText: null,
    snapshotDiff: null,
    totalDuration: null,
    stepDurations: {},
    streamingStartedAt: null,
    streamingEndedAt: null,
    completionData: null,
    diagnostics: null,
    componentManifestEvents: [],
  };
}

// --- Store ---

export const useDebugStore = create<DebugStoreState & DebugStoreActions>()(
  immer((set, get) => ({
    // State
    isEnabled: false,
    currentSession: null,
    sessions: [],
    compareSessionId: null,
    classificationDivergences: [],
    planDebugData: null,

    // Actions
    enable: () => set(state => { state.isEnabled = true; }),
    disable: () => {
      // Clear pending events
      if (_flushTimer) { clearTimeout(_flushTimer); _flushTimer = null; }
      _pendingEvents = [];
      set(state => {
        state.isEnabled = false;
        state.currentSession = null;
      });
    },

    startSession: (userMessage, routeDecision) => {
      if (!get().isEnabled) return;

      // Finalize prior session if exists
      const prior = get().currentSession;
      if (prior) {
        get().endSession();
      }

      set(state => {
        state.currentSession = createEmptySession(userMessage, routeDecision);
      });
    },

    setEditorContext: (ctx) => {
      set(state => {
        if (state.currentSession) state.currentSession.editorContext = ctx;
      });
    },

    setSessionContext: (ctx) => {
      set(state => {
        if (state.currentSession) state.currentSession.sessionContext = ctx;
      });
    },

    setRoutePatternMatched: (input, context) => {
      if (!get().isEnabled) return;
      replayPatternMatch(input, context).then(matched => {
        set(state => {
          if (state.currentSession) state.currentSession.routePatternMatched = matched;
        });
      });
    },

    pushEvent: (event) => {
      if (!get().isEnabled) return;
      _pendingEvents.push(event);

      // Debounce flush
      if (_flushTimer) clearTimeout(_flushTimer);
      _flushTimer = setTimeout(() => {
        get().flushEvents();
      }, FLUSH_INTERVAL);
    },

    flushEvents: () => {
      if (_pendingEvents.length === 0) return;
      const events = [..._pendingEvents];
      _pendingEvents = [];
      _flushTimer = null;

      set(state => {
        if (state.currentSession) {
          state.currentSession.sseEvents.push(...events);

          // Extract step durations and diagnostics from events
          for (const e of events) {
            if (e.type === 'step_complete' && e.data && typeof e.data === 'object') {
              const d = e.data as { step?: string; duration_ms?: number };
              if (d.step && d.duration_ms) {
                state.currentSession.stepDurations[d.step] = d.duration_ms;
              }
            }
            if (e.type === 'diagnostic' && e.data && typeof e.data === 'object') {
              state.currentSession.diagnostics = e.data as DebugSession['diagnostics'];
            }
          }
        }
      });
    },

    recordApplicatorResult: (step, result) => {
      if (!get().isEnabled) return;
      set(state => {
        if (!state.currentSession) return;
        state.currentSession.applicatorResults.push({
          step,
          applied: result.applied,
          failed: result.failed,
          cloningDetails: result.cloningDetails,
          timestamp: Date.now(),
        });
        state.currentSession.actionsApplied.push(...result.applied);
        state.currentSession.actionsFailed.push(...result.failed);
      });
    },

    setStreamingStart: () => {
      set(state => {
        if (state.currentSession) {
          state.currentSession.streamingStartedAt = Date.now();
        }
      });
    },

    setStreamingEnd: () => {
      set(state => {
        if (state.currentSession) {
          state.currentSession.streamingEndedAt = Date.now();
          if (state.currentSession.streamingStartedAt) {
            state.currentSession.totalDuration =
              state.currentSession.streamingEndedAt - state.currentSession.streamingStartedAt;
          }
        }
      });
    },

    setReasoningChain: (chain) => {
      set(state => {
        if (!state.currentSession) return;
        state.currentSession.reasoningChain = chain;
        // Extract confidence distribution from the actions in SSE events
        for (const step of chain) {
          if (step.confidence !== undefined) {
            state.currentSession.confidenceDistribution.push({
              actionType: step.step,
              confidence: step.confidence,
              agentDomain: step.agent,
            });
          }
        }
      });
    },

    setCompletionData: (response) => {
      set(state => {
        if (state.currentSession) {
          state.currentSession.completionData = response;
        }
      });
    },

    setFeedbackSignal: (signal, correctionText) => {
      set(state => {
        if (state.currentSession) {
          state.currentSession.feedbackSignal = signal;
          if (correctionText) state.currentSession.correctionText = correctionText;
        }
      });
    },

    setThreadId: (threadId) => {
      set(state => {
        if (state.currentSession) {
          state.currentSession.threadId = threadId;
        }
      });
    },

    endSession: () => {
      // Suppress during plan mode — routeToVisualEditorStreaming calls this in finally
      if (_planModeActive) return;

      // Flush any pending events first
      if (_pendingEvents.length > 0) {
        get().flushEvents();
      }

      const session = get().currentSession;
      if (!session) return;

      set(state => {
        // Add to sessions list (FIFO eviction)
        state.sessions.push(session);
        if (state.sessions.length > MAX_SESSIONS) {
          state.sessions = state.sessions.slice(state.sessions.length - MAX_SESSIONS);
        }
        state.currentSession = null;
      });

      // Persist to localStorage
      saveToStorage(get().sessions);
    },

    loadSession: (id) => {
      const session = get().sessions.find(s => s.id === id);
      if (session) {
        set(state => {
          state.currentSession = session;
        });
      }
    },

    deleteSession: (id) => {
      set(state => {
        state.sessions = state.sessions.filter(s => s.id !== id);
        if (state.compareSessionId === id) state.compareSessionId = null;
      });
      saveToStorage(get().sessions);
    },

    clearAllSessions: () => {
      set(state => {
        state.sessions = [];
        state.compareSessionId = null;
      });
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    },

    setCompareSession: (id) => {
      set(state => { state.compareSessionId = id; });
    },

    hydrateFromStorage: () => {
      const sessions = loadFromStorage();
      if (sessions.length > 0) {
        set(state => { state.sessions = sessions; });
      }
    },

    recordClassificationDivergence: (divergence) => set(state => {
      state.classificationDivergences.push({ ...divergence, timestamp: Date.now() });
      // Keep max 200 divergences
      if (state.classificationDivergences.length > 200) {
        state.classificationDivergences = state.classificationDivergences.slice(-200);
      }
    }),

    clearClassificationDivergences: () => set(state => {
      state.classificationDivergences = [];
    }),

    pushComponentManifestEvent: (event) => set(state => {
      if (!state.currentSession) return;
      state.currentSession.componentManifestEvents.push({ ...event, timestamp: Date.now() });
    }),

    // ── Plan Debug Actions ──────────────────────────────────────────────

    initPlanDebug: () => {
      _planModeActive = true;
      set(state => {
        state.planDebugData = {
          pipelineTimeline: [],
          phaseExecutions: {},
          qualityGates: [],
          stagePrompts: [],
          runtimeDiagnostics: [],
          formSubmitDiagnostics: [],
          queryTemplateDiagnostics: [],
          pipelineStartedAt: Date.now(),
          pipelineEndedAt: null,
          isActive: true,
        };
      });
    },

    endPlanDebug: () => {
      _planModeActive = false;
      set(state => {
        if (!state.planDebugData) return;
        state.planDebugData.pipelineEndedAt = Date.now();
        state.planDebugData.isActive = false;
        // Close any open timeline stages
        for (const stage of state.planDebugData.pipelineTimeline) {
          if (stage.endedAt === null) {
            stage.endedAt = Date.now();
            stage.duration = stage.endedAt - stage.startedAt;
          }
        }
      });
    },

    recordPlanStageTransition: (stage, label) => {
      if (!get().isEnabled) return;
      const now = Date.now();
      set(state => {
        if (!state.planDebugData) return;
        // Close previous open stage
        for (const s of state.planDebugData.pipelineTimeline) {
          if (s.endedAt === null) {
            s.endedAt = now;
            s.duration = now - s.startedAt;
          }
        }
        // Open new stage
        state.planDebugData.pipelineTimeline.push({
          stage,
          label,
          startedAt: now,
          endedAt: null,
          duration: null,
        });
      });
    },

    completePlanStage: (stage) => {
      if (!get().isEnabled) return;
      const now = Date.now();
      set(state => {
        if (!state.planDebugData) return;
        const entry = state.planDebugData.pipelineTimeline.find(
          s => s.stage === stage && s.endedAt === null
        );
        if (entry) {
          entry.endedAt = now;
          entry.duration = now - entry.startedAt;
        }
      });
    },

    initPhaseExecution: (phaseId, title, domain, enrichedPrompt) => {
      if (!get().isEnabled) return;
      set(state => {
        if (!state.planDebugData) return;
        state.planDebugData.phaseExecutions[phaseId] = {
          phaseId,
          phaseTitle: title,
          domain,
          enrichedPrompt,
          startedAt: Date.now(),
          endedAt: null,
          duration: null,
          subAgentEvents: [],
          subAgentSteps: [],
          actionsProduced: 0,
          componentIdsCreated: [],
          pageIdsCreated: [],
          implAuditResult: null,
          retried: false,
          subAgentThreadId: null,
          subAgentDiagnostics: null,
          error: null,
        };
      });
    },

    pushPhaseSubAgentEvent: (phaseId, event) => {
      if (!get().isEnabled) return;
      set(state => {
        if (!state.planDebugData) return;
        const phase = state.planDebugData.phaseExecutions[phaseId];
        if (!phase) return;
        phase.subAgentEvents.push(event);
        // Extract thread ID from thread events
        if (event.type === 'thread' && event.data && typeof event.data === 'object') {
          const d = event.data as { threadId?: string };
          if (d.threadId) phase.subAgentThreadId = d.threadId;
        }
        // Extract diagnostics + classification (Debug 1)
        if (event.type === 'diagnostic' && event.data && typeof event.data === 'object') {
          phase.subAgentDiagnostics = event.data as Record<string, unknown>;
          const diag = event.data as Record<string, unknown>;
          phase.classification = {
            pipeline: String(diag.pipeline || diag.pipelineType || ''),
            complexity: String(diag.complexity || ''),
            isFollowUp: Boolean(diag.isFollowUp || diag.isSingleAgent),
            followUpAgent: diag.followUpAgent ? String(diag.followUpAgent) : (diag.isSingleAgent ? String(diag.agentType || 'component') : undefined),
            mqttConnected: typeof diag.mqttConnected === 'boolean' ? diag.mqttConnected : undefined,
            classificationSource: String(diag.classificationSource || diag.source || ''),
          };
        }
        // Extract data discovery (Debug 2)
        if (event.type === 'step_complete' && event.data && typeof event.data === 'object') {
          const d = event.data as Record<string, unknown>;
          if (d.step === 'Data Discovery') {
            phase.dataDiscovery = {
              attempted: true,
              duration_ms: typeof d.duration_ms === 'number' ? d.duration_ms : 0,
              matchedSchemas: 0,
              failed: false,
            };
          }
          // Enhancement F: Attach per-step token usage to matching subAgentStep
          if (d.tokenUsage && typeof d.tokenUsage === 'object') {
            const tu = d.tokenUsage as Record<string, unknown>;
            const stepName = String(d.step || '');
            const match = phase.subAgentSteps.find(s => s.step === stepName && !s.tokenUsage);
            if (match) {
              match.tokenUsage = {
                tokensInput: typeof tu.tokensInput === 'number' ? tu.tokensInput : undefined,
                tokensOutput: typeof tu.tokensOutput === 'number' ? tu.tokensOutput : undefined,
                estimatedCostUsd: typeof tu.estimatedCostUsd === 'number' ? tu.estimatedCostUsd : undefined,
              };
            }
          }
        }
        if (event.type === 'message' && event.data && typeof event.data === 'object') {
          const d = event.data as { text?: string };
          if (d.text) {
            if (phase.dataDiscovery) {
              const match = d.text.match(/Found (\d+) matching data schemas/);
              if (match) phase.dataDiscovery.matchedSchemas = parseInt(match[1], 10);
            }
            // Capture diagnostic messages from sub-agents (e.g., [DC Agent Input],
            // [Interaction Agent LLM Preview], etc.)
            if (
              d.text.startsWith('[DC Agent') ||
              d.text.startsWith('[Data Component') ||
              d.text.startsWith('[Interaction Agent')
            ) {
              if (!phase.diagnosticMessages) phase.diagnosticMessages = [];
              phase.diagnosticMessages.push(d.text);
            }
          }
        }
        // Extract verification results (Debug 5)
        if (event.type === 'verification_result' && event.data && typeof event.data === 'object') {
          const d = event.data as Record<string, unknown>;
          if (!phase.verificationResults) phase.verificationResults = [];
          phase.verificationResults.push({
            step: String(d.step || ''),
            passed: Boolean(d.passed),
            issues: Array.isArray(d.issues)
              ? (d.issues as Array<{ message?: string }>).map(issue =>
                  typeof issue === 'object' && issue?.message ? issue.message : String(issue)
                )
              : [],
            componentCount: typeof d.componentCount === 'number' ? d.componentCount : undefined,
            containerCount: typeof d.containerCount === 'number' ? d.containerCount : undefined,
          });
        }
        // Extract token usage from complete event (Debug 4)
        if (event.type === 'complete' && event.data && typeof event.data === 'object') {
          const d = event.data as { metadata?: { usage?: { tokensInput?: number; tokensOutput?: number; estimatedCostUsd?: number } } };
          if (d.metadata?.usage) {
            phase.tokenUsage = {
              tokensInput: d.metadata.usage.tokensInput || 0,
              tokensOutput: d.metadata.usage.tokensOutput || 0,
              estimatedCostUsd: d.metadata.usage.estimatedCostUsd || 0,
            };
          }
        }
      });
    },

    recordPhaseSubAgentStep: (phaseId, step) => {
      if (!get().isEnabled) return;
      set(state => {
        if (!state.planDebugData) return;
        const phase = state.planDebugData.phaseExecutions[phaseId];
        if (!phase) return;
        phase.subAgentSteps.push(step);
      });
    },

    recordPhaseRetry: (phaseId, retry) => {
      if (!get().isEnabled) return;
      set(state => {
        if (!state.planDebugData) return;
        const phase = state.planDebugData.phaseExecutions[phaseId];
        if (!phase) return;
        if (!phase.retries) phase.retries = [];
        phase.retries.push({
          step: retry.step,
          agent: retry.agent,
          attemptNumber: retry.attemptNumber,
          retryType: retry.retryType,
          startedAt: Date.now(),
          durationMs: null,
          actionCount: null,
          accepted: null,
          potentialFalsePositive: Boolean(retry.potentialFalsePositive),
          reason: retry.reason,
          previousAttempt: retry.previousAttempt,
          gateValues: retry.gateValues,
        });
        phase.retried = true;
      });
    },

    recordPhaseRetryResult: (phaseId, result) => {
      if (!get().isEnabled) return;
      set(state => {
        if (!state.planDebugData) return;
        const phase = state.planDebugData.phaseExecutions[phaseId];
        if (!phase || !phase.retries) return;
        // Find matching retry by attemptNumber + step (retries are ordered, so this is a reverse-find)
        for (let i = phase.retries.length - 1; i >= 0; i--) {
          const r = phase.retries[i];
          if (r.attemptNumber === result.attemptNumber && r.step === result.step) {
            r.durationMs = result.durationMs;
            r.actionCount = result.actionCount;
            r.accepted = result.acceptedAsResult;
            if (result.metrics) r.metrics = result.metrics;
            break;
          }
        }
      });
    },

    recordPhaseAttemptsSummary: (phaseId, summary) => {
      if (!get().isEnabled) return;
      set(state => {
        if (!state.planDebugData) return;
        const phase = state.planDebugData.phaseExecutions[phaseId];
        if (!phase) return;
        phase.retrySummary = summary;
      });
    },

    recordPhasePipelineTiming: (phaseId, timing) => {
      if (!get().isEnabled) return;
      set(state => {
        if (!state.planDebugData) return;
        const phase = state.planDebugData.phaseExecutions[phaseId];
        if (!phase) return;
        phase.pipelineTiming = timing;
      });
    },

    completePhaseExecution: (phaseId, data) => {
      if (!get().isEnabled) return;
      const now = Date.now();
      set(state => {
        if (!state.planDebugData) return;
        const phase = state.planDebugData.phaseExecutions[phaseId];
        if (!phase) return;
        phase.endedAt = now;
        phase.duration = now - phase.startedAt;
        if (data.implAuditResult !== undefined) phase.implAuditResult = data.implAuditResult as Record<string, unknown> | null;
        if (data.retried !== undefined) phase.retried = data.retried;
        if (data.actionsProduced !== undefined) phase.actionsProduced = data.actionsProduced;
        if (data.componentIds) phase.componentIdsCreated = data.componentIds;
        if (data.pageIds) phase.pageIdsCreated = data.pageIds;
        if (data.error) phase.error = data.error;
        if (data.actionBreakdown) phase.actionBreakdown = data.actionBreakdown;
        if (data.unresolvedTokens) phase.unresolvedTokens = data.unresolvedTokens;
      });
    },

    setPhaseUnresolvedTokens: (phaseId, tokens) => {
      if (!get().isEnabled) return;
      set(state => {
        if (!state.planDebugData) return;
        const phase = state.planDebugData.phaseExecutions[phaseId];
        if (phase) phase.unresolvedTokens = tokens;
      });
    },

    recordPlanQualityGate: (gate) => {
      if (!get().isEnabled) return;
      set(state => {
        if (!state.planDebugData) return;
        state.planDebugData.qualityGates.push({ ...gate, timestamp: Date.now() });
      });
    },

    recordPlanStagePrompts: (calls) => {
      if (!get().isEnabled) return;
      set(state => {
        if (!state.planDebugData) return;
        state.planDebugData.stagePrompts.push(...calls);
      });
    },

    appendActionSummaries: (phaseId, summaries) => {
      if (!get().isEnabled) return;
      set(state => {
        if (!state.planDebugData) return;
        const phase = state.planDebugData.phaseExecutions[phaseId];
        if (!phase) return;
        if (!phase.actionSummaries) phase.actionSummaries = [];
        phase.actionSummaries.push(...summaries);
      });
    },

    replaceActionSummaries: (phaseId, summaries) => {
      if (!get().isEnabled) return;
      set(state => {
        if (!state.planDebugData) return;
        const phase = state.planDebugData.phaseExecutions[phaseId];
        if (!phase) return;
        phase.actionSummaries = summaries;
      });
    },

    setSchemaQueryAlignment: (phaseId, alignment) => {
      if (!get().isEnabled) return;
      set(state => {
        if (!state.planDebugData) return;
        const phase = state.planDebugData.phaseExecutions[phaseId];
        if (!phase) return;
        phase.schemaQueryAlignment = alignment;
      });
    },

    recordRuntimeDataTextDiagnostic: (diagnostic) => {
      if (!get().isEnabled) return;
      set(state => {
        if (!state.planDebugData) return;
        state.planDebugData.runtimeDiagnostics.push({
          ...diagnostic,
          timestamp: Date.now(),
        });
        if (state.planDebugData.runtimeDiagnostics.length > MAX_RUNTIME_DIAGNOSTICS) {
          state.planDebugData.runtimeDiagnostics = state.planDebugData.runtimeDiagnostics.slice(-MAX_RUNTIME_DIAGNOSTICS);
        }
      });
    },

    recordRuntimeFormSubmitDiagnostic: (diagnostic) => {
      if (!get().isEnabled) return;
      set(state => {
        if (!state.planDebugData) return;
        state.planDebugData.formSubmitDiagnostics.push({
          ...diagnostic,
          timestamp: Date.now(),
        });
        if (state.planDebugData.formSubmitDiagnostics.length > MAX_FORM_SUBMIT_DIAGNOSTICS) {
          state.planDebugData.formSubmitDiagnostics = state.planDebugData.formSubmitDiagnostics.slice(-MAX_FORM_SUBMIT_DIAGNOSTICS);
        }
      });
    },

    recordRuntimeQueryTemplateDiagnostic: (diagnostic) => {
      if (!get().isEnabled) return;
      set(state => {
        if (!state.planDebugData) return;
        if (!state.planDebugData.queryTemplateDiagnostics) {
          state.planDebugData.queryTemplateDiagnostics = [];
        }
        state.planDebugData.queryTemplateDiagnostics.push({
          ...diagnostic,
          timestamp: Date.now(),
        });
        if (state.planDebugData.queryTemplateDiagnostics.length > MAX_QUERY_TEMPLATE_DIAGNOSTICS) {
          state.planDebugData.queryTemplateDiagnostics = state.planDebugData.queryTemplateDiagnostics.slice(-MAX_QUERY_TEMPLATE_DIAGNOSTICS);
        }
      });
    },

    clearPlanDebug: () => {
      _planModeActive = false;
      set(state => { state.planDebugData = null; });
    },
  })),
);
